import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Button, Input, Stack, Modal, useModals, ModalProps } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ApiCompensationChangeReason,
  ApiCompensationChangeReasonPayload,
} from "@/types/compensation-change-reason.types.ts";
import {
  SaveParams,
  useCompensationChangeReasonSave,
} from "@/queries/use-compensation-change-reasons.query.ts";

export interface CompensationChangeReasonEditModelProps extends ModalProps {
  compensationChangeReason?: ApiCompensationChangeReason;
}

export function CompensationChangeReasonEditModal({
  compensationChangeReason,
  ...props
}: CompensationChangeReasonEditModelProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };

  const CompensationChangeReasonSchemaObject = z.object({
    title: z.string().min(1, t("errors:field_required")),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ApiCompensationChangeReasonPayload>({
    mode: "onSubmit",
    resolver: zodResolver(CompensationChangeReasonSchemaObject),
    defaultValues: {
      title: compensationChangeReason?.title ?? "",
    },
  });

  const compensationChangeReasonSave = useCompensationChangeReasonSave(control);

  const onSubmit = handleSubmit(async (data) => {
    const payload: SaveParams = {
      id: compensationChangeReason?.id,
      title: data.title,
    };

    try {
      await compensationChangeReasonSave.mutateAsync(payload, {
        onSuccess: (data) => {
          if (compensationChangeReason) {
            analyticsService.trackEvent(analyticEvents.compensationChangeReasons.edited, {
              [analyticProperties.id]: compensationChangeReason.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.compensationChangeReasons.created, {
              [analyticProperties.id]: data.id,
            });
          }

          close(id);
        },
      });
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t("core:compensation_change_reason")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={compensationChangeReasonSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
